import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { isSageIDUser, QUERY_PARAMS, ROUTES } from '@futrli/shared-services';

import { OnboardingContainer } from '../containers/onboarding-container';
import { OnboardingIntegrationsContainer } from '../containers/onboarding-integrations-container';
import { OnboardingStart } from './onboarding-start';
import { OnboardingInitialSync } from './onboarding-initial-sync/onboarding-initial-sync';
import { OnboardingSageOrgs } from './onboarding-sage-orgs';
import { ForecastingOnboardingStart } from './forecast-onboarding-start';
import { BudgetDateSelect } from './budget-date-select';
import { OnboardingNavbar } from './onboarding-navbar';
import { InitialOnboardingNav } from './initial-onboarding-nav';
import { ScenarioSelectType } from './scenario-select-type';
import { XeroAdditional } from './xero-additional';
import { SageIdAdditional } from './sage-id-additional';
import { SageidOnboardingContainer } from '../containers/sageid-onboarding-container';
import { AuthStoreState } from '../../../../predict/src/core/types';
import { SfaOnboardingContainer } from '../containers/sfa/sfa-onboarding-container';
import { SfaOnboardingContextProvider } from '../contexts/sfa-onboarding-context';

const OnboardingInitialManager = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);

  if (!workspaceId || !organisationId) {
    return null;
  }

  return (
    <>
      <OnboardingNavbar />
      <InitialOnboardingNav />
      <Routes>
        <Route path={ROUTES.FORECAST_START} element={<ForecastingOnboardingStart />} />
        <Route path={ROUTES.BUDGET_DATE_SELECT} element={<BudgetDateSelect />} />
        <Route path={ROUTES.SCENARIO_TYPE} element={<ScenarioSelectType />} />
      </Routes>
    </>
  );
};

export const OnboardingManager = (): JSX.Element => {
  // We need this since Xero users need to continue to work with Cognito
  const sageIdUser = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    return isSageIDUser(user);
  });

  return (
    <Routes>
      <Route
        path={ROUTES.SFA}
        element={
          <SfaOnboardingContextProvider>
            <SfaOnboardingContainer />
          </SfaOnboardingContextProvider>
        }
      />

      <Route path={ROUTES.XERO_ADDITIONAL} element={<XeroAdditional />} />
      <Route path={ROUTES.ADDITIONAL_INFO} element={<SageIdAdditional />} />
      <Route path={ROUTES.INITIAL_SYNC} element={<OnboardingInitialSync />} />
      <Route path={`${ROUTES.ONBOARDING_INITIAL}/*`} element={<OnboardingInitialManager />} />
      <Route path={ROUTES.SAGE_ORGS} element={<OnboardingSageOrgs />} />
      <Route path={ROUTES.START} element={<OnboardingStart />} />
      <Route path={ROUTES.INTEGRATIONS_ROUTE} element={<OnboardingIntegrationsContainer />} />

      <Route
        path={ROUTES.PRODUCT_SLUG}
        element={sageIdUser ? <SageidOnboardingContainer /> : <OnboardingContainer />}
      />

      <Route path="/" element={sageIdUser ? <SageidOnboardingContainer /> : <OnboardingContainer />} />

      <Route path="*" element={<Navigate to={ROUTES.ONBOARDING_ROUTE} replace />} />
    </Routes>
  );
};
